import http from '../http-common';
import { buildRunOptions } from '../utils/engineRules';
import { buildRunOptionsBody, withRunParam } from './analysisRunOptions';

class ManualAnalysisService {
  /**
   * Get persisted UI preferences for manual analysis list
   * @returns {Promise}
   */
  getUiPreferences() {
    return http.get('/manual-analysis/ui-preferences');
  }

  /**
   * Persist UI preferences for manual analysis list
   * @param {{ listViewMode: 'table'|'cards', selectedFolderFilter?: string }} payload
   * @returns {Promise}
   */
  setUiPreferences(payload) {
    return http.put('/manual-analysis/ui-preferences', payload);
  }

  /**
   * Upload a video file to S3 and trigger analysis
   * @param {FormData} formData - FormData containing the video file
   * @param {Function} onUploadProgress - Callback for upload progress
   * @returns {Promise} Promise with the analysis ID
   */
  async uploadVideo(formData, onUploadProgress) {
    return http.post('/manual-analysis/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 7200000, // 2 hours timeout for large file uploads (up to 10GB)
      maxContentLength: Infinity,
      maxBodyLength: Infinity,
      onUploadProgress: (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        if (onUploadProgress) {
          onUploadProgress(percentCompleted);
        }
      },
    });
  }

  /**
   * Get analysis status
   * @param {number} analysisId - Analysis ID
   * @param {string|null} run - Optional run id (`?run=`); omitted -> active run
   * @returns {Promise} Promise with the analysis status
   */
  getAnalysisStatus(analysisId, run = null) {
    return http.get(`/manual-analysis/${analysisId}/status`, {
      params: withRunParam({}, run),
    });
  }

  /**
   * Get all manual analyses with pagination
   * @param {number} page - Page number
   * @param {number} limit - Items per page
   * @returns {Promise} Promise with paginated manual analyses
   */
  findAll(page, limit, folderId, search) {
    const params = {
      page: page,
      limit: limit,
    };
    if (folderId !== undefined) {
      params.folderId = folderId;
    }
    if (typeof search === 'string' && search.trim().length > 0) {
      params.search = search.trim();
    }

    return http.get('/manual-analysis', {
      params,
    });
  }

  /**
   * List manual analyses with the M4 filters: `groupId` restricts the page to the members of an
   * analysis group (`?group_id=`) and composes with `folderId` and `search`; `controlKind` does
   * the same for the members of a control (`?control_kind=`, ignored when `groupId` is set).
   * @param {{ page?: number, limit?: number, folderId?: number|string|null, search?: string,
   *   groupId?: number|string|null, controlKind?: string|null }} options
   * @returns {Promise}
   */
  list({ page = 1, limit = 10, folderId, search, groupId, controlKind } = {}) {
    const params = { page, limit };
    if (folderId !== undefined && folderId !== null && folderId !== '') params.folderId = folderId;
    if (typeof search === 'string' && search.trim().length > 0) params.search = search.trim();
    if (groupId !== undefined && groupId !== null && groupId !== '') params.group_id = groupId;
    if (controlKind) params.control_kind = controlKind;
    return http.get('/manual-analysis', { params });
  }

  /**
   * Delete a manual analysis
   * @param {number} analysisId - Analysis ID
   * @returns {Promise} Promise
   */
  delete(analysisId) {
    return http.delete(`/manual-analysis/${analysisId}`);
  }

  /**
   * Delete manual analyses in bulk
   * @param {number[]} analysisIds - Analysis IDs to delete
   * @returns {Promise} Promise
   */
  bulkDelete(analysisIds) {
    return http.post('/manual-analysis/bulk-delete', {
      analysisIds,
    });
  }

  getFolders() {
    return http.get('/manual-analysis/folders');
  }

  createFolder(name) {
    return http.post('/manual-analysis/folders', { name });
  }

  deleteFolder(folderId) {
    return http.delete(`/manual-analysis/folders/${folderId}`);
  }

  updateAnalysisFolder(analysisId, folderId) {
    return http.put(`/manual-analysis/${analysisId}/folder`, { folderId });
  }

  bulkUpdateFolder(analysisIds, folderId) {
    return http.put('/manual-analysis/bulk-folder', { analysisIds, folderId });
  }

  /**
   * Queue a re-analysis.
   * @param {number} analysisId - Analysis ID
   * @param {{ socketId?: string, analysisTypeId?: number }} payload
   * @param {{ batch?: string, note?: string, promoteOnSuccess?: boolean }} [runOptions] optional
   *   per-run options persisted on the ledger
   * @returns {Promise}
   */
  reanalyze(analysisId, payload = {}, runOptions = null) {
    const { runOptions: engineOptions, ...legacyPayload } = payload || {};
    if (!engineOptions) {
      return http.post(`/manual-analysis/${analysisId}/reanalyze`, {
        ...legacyPayload,
        ...buildRunOptionsBody(runOptions),
      });
    }
    // M3 contract 8: camelCase `runOptions` block; batch/note/promote also flat for M1 readers.
    const options = buildRunOptions(engineOptions);
    return http.post(`/manual-analysis/${analysisId}/reanalyze`, {
      ...legacyPayload,
      ...buildRunOptionsBody(options),
      runOptions: options,
    });
  }

  /**
   * Queue several re-analyses in one shared batch (M3 contract 7):
   * `POST /manual-analysis/bulk-reanalyze { ids, analysisTypeId?, socketId?, runOptions }` ->
   * 202 `{ batch, accepted: [{ id, run_id, position }], rejected: [{ id, reason }] }`.
   * @param {number[]} ids
   * @param {{ engines: string[], configVersionId?: string|null, batch?: string|null,
   *   note?: string|null, promoteOnSuccess?: boolean|null }} runOptions
   * @param {{ analysisTypeId?: number, socketId?: string }} [extra]
   * @returns {Promise}
   */
  bulkReanalyze(ids, runOptions, extra = {}) {
    const body = {
      ids: (ids || []).map((id) => Number(id)),
      runOptions: buildRunOptions(runOptions || {}),
    };
    if (extra.analysisTypeId !== undefined && extra.analysisTypeId !== null) {
      body.analysisTypeId = extra.analysisTypeId;
    }
    if (typeof extra.socketId === 'string' && extra.socketId) body.socketId = extra.socketId;
    return http.post('/manual-analysis/bulk-reanalyze', body);
  }
}

export default new ManualAnalysisService();
